/**
 * Count Difference Report — "What was wrong in this count?"
 * Pick a stock count and see, item by item, what the system said vs what was
 * actually found on the shelf. Searchable, and filterable to short / extra only.
 * The count list is scoped to the branch selected in the header.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FiPackage, FiAlertCircle, FiTrendingDown, FiTrendingUp, FiDownload, FiPrinter, FiSearch } from 'react-icons/fi';
import PageLayout from '../../components/PageLayout';
import { useAuth } from '../../context/AuthContext';
import useCurrency from '../../hooks/useCurrency';
import { colors } from '../../theme/colors';
import { loadAudits, loadAuditLines } from '../../utils/stockAuditHelper';
import {
  SummaryCards, SummaryCard, SummaryIcon, SummaryInfo, SummaryLabel, SummaryValue,
  FiltersCard, FiltersRow, FilterGroup, Label, Input, Select, ButtonGroup, Button,
  ContentCard, TableContainer, Table, Th, Td, Tr, TotalRow, EmptyState,
  Short, Extra, SearchWrap, printReport, downloadCsv, csvCell, fmtDateTime, duration,
} from './auditReportStyles';

const CountDifferenceReport = () => {
  const { activeCompany, activeBranch } = useAuth();
  const { formatCurrency } = useCurrency();
  const companyId = activeCompany?.id;

  const [audits, setAudits] = useState([]);
  const [auditId, setAuditId] = useState('');
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [itemSearch, setItemSearch] = useState('');
  const [itemType, setItemType] = useState('diff'); // diff | short | extra | all
  const [minValue, setMinValue] = useState('');     // only differences worth >= this

  useEffect(() => {
    if (!companyId) return;
    loadAudits(companyId)
      .then(a => {
        const done = a
          .filter(x => x.status !== 'Counting' && x.status !== 'Cancelled')
          .filter(x => !activeBranch || activeBranch.id === 'all' || x.branchId === activeBranch.id);
        setAudits(done);
        setAuditId(prev => (done.some(d => d.id === prev) ? prev : (done[0]?.id || '')));
      })
      .catch(console.error);
  }, [companyId, activeBranch]);

  const audit = useMemo(() => audits.find(a => a.id === auditId) || null, [audits, auditId]);

  const load = useCallback(async () => {
    if (!companyId || !auditId) { setLines([]); return; }
    setLoading(true);
    try {
      const ls = await loadAuditLines(companyId, auditId);
      ls.sort((a, b) => Math.abs(Number(b.variance) || 0) - Math.abs(Number(a.variance) || 0));
      setLines(ls);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [companyId, auditId]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const q = itemSearch.trim().toLowerCase();
    const min = Number(minValue) || 0;
    return lines.filter(l => {
      const v = Number(l.variance) || 0;
      const value = Math.abs(v) * (Number(l.unitCost) || 0);
      if (itemType === 'diff' && Math.abs(v) <= 0.0001) return false;
      if (itemType === 'short' && v >= -0.0001) return false;
      if (itemType === 'extra' && v <= 0.0001) return false;
      if (min && value < min) return false;
      if (q && !(`${l.itemName || ''} ${l.itemCode || ''} ${l.barcode || ''}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [lines, itemSearch, itemType, minValue]);

  // KPI totals are for the WHOLE count (not the filtered view).
  const totals = useMemo(() => lines.reduce((t, l) => {
    const v = Number(l.variance) || 0;
    const val = Math.abs(v) * (Number(l.unitCost) || 0);
    if (Math.abs(v) > 0.0001) { t.withDiff++; if (v < 0) t.short += val; else t.extra += val; }
    return t;
  }, { withDiff: 0, short: 0, extra: 0 }), [lines]);

  // Totals for what is currently shown.
  const shown = useMemo(() => rows.reduce((t, l) => {
    const v = Number(l.variance) || 0;
    const val = Math.abs(v) * (Number(l.unitCost) || 0);
    if (v < 0) t.short += val; else if (v > 0) t.extra += val;
    return t;
  }, { short: 0, extra: 0 }), [rows]);

  const meta = audit
    ? `${activeCompany?.companyName || ''} | ${audit.branchName ? audit.branchName + ' · ' : ''}${audit.locationName} | Started ${fmtDateTime(audit.startedAt)} | Ended ${fmtDateTime(audit.submittedAt)} (${duration(audit.startedAt, audit.submittedAt)})`
    : '';

  const handleExport = () => {
    let csv = 'Item,Code,System Qty,Counted Qty,Difference,Value\n';
    rows.forEach(l => {
      const v = Number(l.variance) || 0;
      csv += [
        csvCell(l.itemName), csvCell(l.itemCode || ''),
        l.bookQty ?? 0, l.countedQty ?? 0, v,
        Math.round(Math.abs(v) * (Number(l.unitCost) || 0) * 100) / 100,
      ].join(',') + '\n';
    });
    downloadCsv(`Count_Difference_${(audit?.locationName || 'audit').replace(/\W+/g, '_')}.csv`, csv);
  };

  const handlePrint = () => {
    const body = rows.map(l => {
      const v = Number(l.variance) || 0;
      return `<tr>
        <td>${l.itemName || ''} ${l.notCounted ? '<b>(not counted)</b>' : ''}</td>
        <td>${l.itemCode || ''}</td>
        <td class="r">${l.bookQty ?? 0}</td>
        <td class="r">${l.countedQty ?? 0}</td>
        <td class="r ${v < 0 ? 'short' : 'extra'}">${v > 0 ? '+' : ''}${v}</td>
        <td class="r">${formatCurrency(Math.abs(v) * (Number(l.unitCost) || 0))}</td>
      </tr>`;
    }).join('');
    printReport('Count Difference Report', meta,
      `<table><thead><tr><th>Item</th><th>Code</th><th class="r">System Qty</th>
        <th class="r">Counted Qty</th><th class="r">Difference</th><th class="r">Value</th>
       </tr></thead><tbody>${body}</tbody>
       <tfoot><tr><td colspan="5">Stock Short / Stock Extra (shown)</td>
         <td class="r"><span class="short">${formatCurrency(shown.short)}</span> / <span class="extra">${formatCurrency(shown.extra)}</span></td>
       </tr></tfoot></table>`);
  };

  return (
    <PageLayout pageTitle="Count Difference Report" breadcrumb="Reports / Audit Reports / Count Difference Report">
      <SummaryCards>
        <SummaryCard>
          <SummaryIcon color={colors.primary.main}><FiPackage /></SummaryIcon>
          <SummaryInfo><SummaryLabel>Items Counted</SummaryLabel><SummaryValue>{audit?.totals?.itemsCounted ?? 0}</SummaryValue></SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIcon color="#1565C0"><FiAlertCircle /></SummaryIcon>
          <SummaryInfo><SummaryLabel>Items With Difference</SummaryLabel><SummaryValue>{totals.withDiff}</SummaryValue></SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIcon color={colors.error.main}><FiTrendingDown /></SummaryIcon>
          <SummaryInfo><SummaryLabel>Stock Short</SummaryLabel>
            <SummaryValue style={{ color: colors.error.main }}>{formatCurrency(totals.short)}</SummaryValue></SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIcon color={colors.success.main}><FiTrendingUp /></SummaryIcon>
          <SummaryInfo><SummaryLabel>Stock Extra</SummaryLabel>
            <SummaryValue style={{ color: colors.success.main }}>{formatCurrency(totals.extra)}</SummaryValue></SummaryInfo>
        </SummaryCard>
      </SummaryCards>

      <FiltersCard>
        <FiltersRow>
          <FilterGroup style={{ minWidth: 380 }}>
            <Label>Stock Count</Label>
            <Select value={auditId} onChange={e => setAuditId(e.target.value)}>
              {audits.length === 0 && <option value="">No counts yet</option>}
              {audits.map(a => (
                <option key={a.id} value={a.id}>
                  {fmtDateTime(a.startedAt)} — {a.branchName ? a.branchName + ' · ' : ''}{a.locationName} ({a.status})
                </option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup style={{ minWidth: 240 }}>
            <Label>Search Item</Label>
            <SearchWrap>
              <FiSearch size={14} />
              <Input value={itemSearch} onChange={e => setItemSearch(e.target.value)} placeholder="Item name, code or barcode…" />
            </SearchWrap>
          </FilterGroup>
          <FilterGroup>
            <Label>Show</Label>
            <Select value={itemType} onChange={e => setItemType(e.target.value)}>
              <option value="diff">Only differences</option>
              <option value="short">Only short items</option>
              <option value="extra">Only extra items</option>
              <option value="all">All counted items</option>
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Min. Value</Label>
            <Input type="number" min="0" value={minValue} onChange={e => setMinValue(e.target.value)} placeholder="e.g. 500" />
          </FilterGroup>
          <ButtonGroup>
            <Button onClick={handleExport} disabled={!rows.length}><FiDownload /> CSV</Button>
            <Button onClick={handlePrint} disabled={!rows.length}><FiPrinter /> Print</Button>
          </ButtonGroup>
        </FiltersRow>
        {audit && (
          <FiltersRow style={{ marginTop: 12, fontSize: 12, color: '#777' }}>
            <span>
              <b>Started:</b> {fmtDateTime(audit.startedAt)} &nbsp;·&nbsp;
              <b>Ended:</b> {audit.submittedAt ? fmtDateTime(audit.submittedAt) : '—'} &nbsp;·&nbsp;
              <b>Took:</b> {duration(audit.startedAt, audit.submittedAt)}
            </span>
          </FiltersRow>
        )}
      </FiltersCard>

      <ContentCard>
        <TableContainer>
          {loading ? <EmptyState>Loading…</EmptyState>
            : !audit ? <EmptyState>No stock counts have been done yet.</EmptyState>
            : rows.length === 0 ? <EmptyState>No items match these filters.</EmptyState> : (
              <Table>
                <thead><tr>
                  <Th>Item</Th><Th>Code</Th>
                  <Th align="right">System Qty</Th><Th align="right">Counted Qty</Th>
                  <Th align="right">Difference</Th><Th align="right">Value</Th>
                </tr></thead>
                <tbody>
                  {rows.map(l => {
                    const v = Number(l.variance) || 0;
                    return (
                      <Tr key={l.itemId}>
                        <Td>
                          {l.itemName}
                          {l.notCounted && <Short style={{ marginLeft: 8, fontSize: 11 }}>NOT COUNTED</Short>}
                        </Td>
                        <Td><small>{l.itemCode}</small></Td>
                        <Td align="right">{l.bookQty ?? 0}</Td>
                        <Td align="right">{l.countedQty ?? 0}</Td>
                        <Td align="right">
                          {v < 0 ? <Short>{v}</Short> : v > 0 ? <Extra>+{v}</Extra> : <span style={{ color: '#999' }}>0</span>}
                        </Td>
                        <Td align="right">{formatCurrency(Math.abs(v) * (Number(l.unitCost) || 0))}</Td>
                      </Tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <TotalRow>
                    <Td colSpan={5} style={{ textAlign: 'right' }}>Shown: Stock Short / Stock Extra</Td>
                    <Td align="right">
                      <Short>{formatCurrency(shown.short)}</Short> / <Extra>{formatCurrency(shown.extra)}</Extra>
                    </Td>
                  </TotalRow>
                </tfoot>
              </Table>
            )}
        </TableContainer>
      </ContentCard>
    </PageLayout>
  );
};

export default CountDifferenceReport;
